/**
 * TaskComments component - displays and manages task comments with threading
 */

import { useState, useEffect, useRef, forwardRef } from 'react';
import { useTranslation } from 'react-i18next';
import { clsx } from 'clsx';
import { MessageSquare, Send, CornerDownRight, MoreHorizontal, Pencil, Trash2 } from 'lucide-react';
import { formatDistanceToNow } from '@/i18n/dateFns';
import Avatar from '@components/ui/Avatar';
import { commentsApi, type Comment, type CreateCommentRequest } from '@services/api';
import { toast } from '@components/ui/Toast';
import { ConfirmDialog } from '@/components/ui/ConfirmDialog';

interface TaskCommentsProps {
  taskId: string;
}

export default function TaskComments({ taskId }: TaskCommentsProps) {
  const { t } = useTranslation();
  const [comments, setComments] = useState<Comment[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [newComment, setNewComment] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [replyingTo, setReplyingTo] = useState<string | null>(null);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [editContent, setEditContent] = useState('');
  const [deletingCommentId, setDeletingCommentId] = useState<string | null>(null);
  const textareaRef = useRef<HTMLTextAreaElement>(null);

  // Load comments on mount
  useEffect(() => {
    let isMounted = true;

    const fetchComments = async () => {
      try {
        setIsLoading(true);
        const data = await commentsApi.getTaskComments(taskId, { includeReplies: true });
        if (isMounted) {
          setComments(data);
        }
      } catch (error) {
        console.error('Failed to load comments:', error);
        if (isMounted) {
          toast.error(t('task.failedLoadComments'));
        }
      } finally {
        if (isMounted) {
          setIsLoading(false);
        }
      }
    };

    void fetchComments();

    return () => {
      isMounted = false;
    };
  }, [taskId, t]);

  const handleSubmitComment = async (parentId?: string) => {
    const content = newComment.trim();
    if (!content || isSubmitting) return;

    setIsSubmitting(true);
    try {
      const request: CreateCommentRequest = { content };
      if (parentId) {
        request.parentId = parentId;
      }
      await commentsApi.createComment(taskId, request);

      // Clear input
      setNewComment('');
      setReplyingTo(null);

      toast.success(t('task.commentAdded'));

      // Reload comments in background - don't block on this
      try {
        const freshComments = await commentsApi.getTaskComments(taskId, { includeReplies: true });
        setComments(freshComments);
      } catch (refreshError) {
        console.error('Failed to refresh comments:', refreshError);
        // Don't show error - comment was added successfully
      }
    } catch (error: unknown) {
      console.error('Failed to add comment:', error);
      const message = error instanceof Error ? error.message :
        (typeof error === 'object' && error !== null && 'message' in error) ? String((error as {message: unknown}).message) :
        t('task.failedAddComment');
      toast.error(message);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleUpdateComment = async (commentId: string) => {
    const content = editContent.trim();
    if (!content) return;

    try {
      const updated = await commentsApi.updateComment(commentId, { content });
      setComments(prev => prev.map(c => c.id === commentId ? { ...c, ...updated } : c));
      setEditingId(null);
      setEditContent('');
      toast.success(t('task.commentUpdated'));
    } catch (error) {
      console.error('Failed to update comment:', error);
      toast.error(t('task.failedUpdateComment'));
    }
  };

  const handleDeleteComment = (commentId: string) => {
    setDeletingCommentId(commentId);
  };

  const confirmDeleteComment = async () => {
    if (!deletingCommentId) return;

    try {
      await commentsApi.deleteComment(deletingCommentId);
      setComments(prev => prev.filter(c => c.id !== deletingCommentId));
      toast.success(t('task.commentDeleted'));
    } catch (error) {
      console.error('Failed to delete comment:', error);
      toast.error(t('task.failedDeleteComment'));
    } finally {
      setDeletingCommentId(null);
    }
  };

  const startEditing = (comment: Comment) => {
    setEditingId(comment.id);
    setEditContent(comment.content);
  };

  const startReply = (commentId: string) => {
    setReplyingTo(commentId);
    setNewComment('');
    setTimeout(() => textareaRef.current?.focus(), 0);
  };

  // Get top-level comments (no parentId)
  const topLevelComments = comments.filter(c => !c.parentId);

  // Get replies for a comment
  const getReplies = (parentId: string) =>
    comments.filter(c => c.parentId === parentId);

  return (
    <div>
      <h3 className="mb-3 flex items-center gap-2 text-sm font-medium text-neutral-500 dark:text-neutral-400">
        <MessageSquare className="h-4 w-4" />
        {t('task.comments')}
        {comments.length > 0 && (
          <span className="text-neutral-400">({comments.length})</span>
        )}
      </h3>

      {/* New comment input */}
      {!replyingTo && (
        <CommentInput
          value={newComment}
          onChange={setNewComment}
          onSubmit={() => handleSubmitComment()}
          isSubmitting={isSubmitting}
          placeholder={t('task.addCommentPlaceholder')}
          ref={textareaRef}
        />
      )}

      {/* Comments list */}
      <div className="mt-4 space-y-4">
        {isLoading ? (
          <div className="py-4 text-center text-sm text-neutral-500">
            {t('task.loadingComments')}
          </div>
        ) : topLevelComments.length === 0 ? (
          <div className="py-4 text-center text-sm text-neutral-500">
            {t('task.noCommentsYet')}
          </div>
        ) : (
          topLevelComments.map(comment => (
            <CommentItem
              key={comment.id}
              comment={comment}
              replies={getReplies(comment.id)}
              isEditing={editingId === comment.id}
              editContent={editContent}
              onEditContentChange={setEditContent}
              onStartEdit={() => startEditing(comment)}
              onCancelEdit={() => { setEditingId(null); setEditContent(''); }}
              onSaveEdit={() => handleUpdateComment(comment.id)}
              onDelete={() => handleDeleteComment(comment.id)}
              onStartReply={() => startReply(comment.id)}
              isReplying={replyingTo === comment.id}
              replyValue={replyingTo === comment.id ? newComment : ''}
              onReplyChange={setNewComment}
              onSubmitReply={() => handleSubmitComment(comment.id)}
              onCancelReply={() => { setReplyingTo(null); setNewComment(''); }}
              isSubmittingReply={isSubmitting}
              // For nested replies
              allComments={comments}
              onDeleteComment={handleDeleteComment}
              onStartEditComment={startEditing}
              editingId={editingId}
              onEditContentChangeForReply={setEditContent}
              onSaveEditForReply={handleUpdateComment}
              onCancelEditForReply={() => { setEditingId(null); setEditContent(''); }}
              editContentForReply={editContent}
            />
          ))
        )}
      </div>

      <ConfirmDialog
        open={deletingCommentId !== null}
        onClose={() => setDeletingCommentId(null)}
        onConfirm={confirmDeleteComment}
        title={t('task.deleteCommentTitle')}
        description={t('task.deleteCommentDescription')}
        confirmLabel={t('common.delete')}
        variant="danger"
      />
    </div>
  );
}

interface CommentInputProps {
  value: string;
  onChange: (value: string) => void;
  onSubmit: () => void;
  isSubmitting: boolean;
  placeholder?: string;
  autoFocus?: boolean;
  onCancel?: () => void;
}

const CommentInput = forwardRef<HTMLTextAreaElement, CommentInputProps>(
  (
    {
      value,
      onChange,
      onSubmit,
      isSubmitting,
      placeholder = 'Write a comment...',
      autoFocus = false,
      onCancel,
    },
    ref
  ) => {
    const { t } = useTranslation();

    const handleKeyDown = (e: React.KeyboardEvent) => {
      if (e.key === 'Enter' && (e.metaKey || e.ctrlKey)) {
        e.preventDefault();
        onSubmit();
      }
      if (e.key === 'Escape' && onCancel) {
        onCancel();
      }
    };

    return (
      <div className="flex gap-2">
        <textarea
          ref={ref}
          value={value}
          onChange={(e) => onChange(e.target.value)}
          onKeyDown={handleKeyDown}
          placeholder={placeholder}
          autoFocus={autoFocus}
          rows={2}
          className={clsx(
            'flex-1 resize-none rounded-lg border p-2 text-sm',
            'border-neutral-300 bg-white dark:border-neutral-700 dark:bg-neutral-800',
            'text-neutral-900 placeholder-neutral-400 dark:text-white dark:placeholder-neutral-500',
            'focus:border-primary-500 focus:outline-none focus:ring-1 focus:ring-primary-500'
          )}
        />
        <div className="flex flex-col gap-1">
          <button
            onClick={onSubmit}
            disabled={!value.trim() || isSubmitting}
            className={clsx(
              'flex h-8 w-8 items-center justify-center rounded-lg',
              'bg-primary-600 text-white hover:bg-primary-700',
              'disabled:cursor-not-allowed disabled:opacity-50'
            )}
            title={t('task.sendTooltip')}
          >
            <Send className="h-4 w-4" />
          </button>
          {onCancel && (
            <button
              onClick={onCancel}
              className="flex h-8 w-8 items-center justify-center rounded-lg text-neutral-500 hover:bg-neutral-100 dark:hover:bg-neutral-800"
              title={t('task.cancelTooltip')}
            >
              ×
            </button>
          )}
        </div>
      </div>
    );
  }
);
CommentInput.displayName = 'CommentInput';

interface CommentItemProps {
  comment: Comment;
  replies: Comment[];
  isEditing: boolean;
  editContent: string;
  onEditContentChange: (value: string) => void;
  onStartEdit: () => void;
  onCancelEdit: () => void;
  onSaveEdit: () => void;
  onDelete: () => void;
  onStartReply: () => void;
  isReplying: boolean;
  replyValue: string;
  onReplyChange: (value: string) => void;
  onSubmitReply: () => void;
  onCancelReply: () => void;
  isSubmittingReply: boolean;
  depth?: number;
  // For nested replies
  allComments: Comment[];
  onDeleteComment: (id: string) => void;
  onStartEditComment: (comment: Comment) => void;
  editingId: string | null;
  onEditContentChangeForReply: (value: string) => void;
  onSaveEditForReply: (id: string) => void;
  onCancelEditForReply: () => void;
  editContentForReply: string;
}

function CommentItem({
  comment,
  replies,
  isEditing,
  editContent,
  onEditContentChange,
  onStartEdit,
  onCancelEdit,
  onSaveEdit,
  onDelete,
  onStartReply,
  isReplying,
  replyValue,
  onReplyChange,
  onSubmitReply,
  onCancelReply,
  isSubmittingReply,
  depth = 0,
  allComments,
  onDeleteComment,
  onStartEditComment,
  editingId,
  onEditContentChangeForReply,
  onSaveEditForReply,
  onCancelEditForReply,
  editContentForReply,
}: CommentItemProps) {
  const { t } = useTranslation();
  const [showMenu, setShowMenu] = useState(false);
  const menuRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(e.target as Node)) {
        setShowMenu(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const timeAgo = comment.createdAt
    ? formatDistanceToNow(new Date(comment.createdAt), { addSuffix: true })
    : 'just now';
  const isSystemComment = comment.commentType === 'system' || comment.commentType === 'activity';

  return (
    <div className={clsx(depth > 0 && 'ml-6 border-l-2 border-neutral-200 pl-4 dark:border-neutral-700')}>
      <div className="group">
        <div className="flex gap-3">
          {/* Avatar */}
          <Avatar
            size="sm"
            name={comment.author?.displayName || 'Unknown'}
            {...(comment.author?.avatarUrl && { src: comment.author.avatarUrl })}
          />

          {/* Content */}
          <div className="flex-1 min-w-0">
            {/* Header */}
            <div className="flex items-center gap-2">
              <span className="text-sm font-medium text-neutral-900 dark:text-white">
                {comment.author?.displayName || 'Unknown'}
              </span>
              <span className="text-xs text-neutral-500">
                {timeAgo}
              </span>
              {comment.editedAt && (
                <span className="text-xs text-neutral-400">{t('task.edited')}</span>
              )}
            </div>

            {/* Body */}
            {isEditing ? (
              <div className="mt-2">
                <textarea
                  value={editContent}
                  onChange={e => onEditContentChange(e.target.value)}
                  className={clsx(
                    'w-full resize-none rounded-lg border p-2 text-sm',
                    'border-neutral-300 dark:border-neutral-700 dark:bg-neutral-800',
                    'focus:border-primary-500 focus:outline-none focus:ring-1 focus:ring-primary-500'
                  )}
                  rows={3}
                  autoFocus
                />
                <div className="mt-2 flex gap-2">
                  <button
                    onClick={onSaveEdit}
                    className="rounded bg-primary-600 px-3 py-1 text-xs text-white hover:bg-primary-700"
                  >
                    {t('common.save')}
                  </button>
                  <button
                    onClick={onCancelEdit}
                    className="rounded px-3 py-1 text-xs text-neutral-600 hover:bg-neutral-100 dark:text-neutral-400 dark:hover:bg-neutral-800"
                  >
                    {t('common.cancel')}
                  </button>
                </div>
              </div>
            ) : (
              <p className={clsx(
                'mt-1 whitespace-pre-wrap text-sm',
                isSystemComment
                  ? 'italic text-neutral-500'
                  : 'text-neutral-700 dark:text-neutral-300'
              )}>
                {comment.content}
              </p>
            )}

            {/* Actions */}
            {!isEditing && !isSystemComment && (
              <div className="mt-2 flex items-center gap-3">
                <button
                  onClick={onStartReply}
                  className="flex items-center gap-1 text-xs text-neutral-500 hover:text-primary-600"
                >
                  <CornerDownRight className="h-3 w-3" />
                  {t('task.replyLabel')}
                </button>

                {/* Menu */}
                <div ref={menuRef} className="relative">
                  <button
                    onClick={() => setShowMenu(!showMenu)}
                    className={clsx(
                      'rounded p-1 text-neutral-400 hover:bg-neutral-100 dark:hover:bg-neutral-800',
                      'opacity-0 transition-opacity group-hover:opacity-100'
                    )}
                  >
                    <MoreHorizontal className="h-4 w-4" />
                  </button>

                  {showMenu && (
                    <div className="absolute left-0 top-full z-10 mt-1 w-32 rounded-lg border border-neutral-200 bg-white py-1 shadow-lg dark:border-neutral-700 dark:bg-neutral-800">
                      <button
                        onClick={() => { onStartEdit(); setShowMenu(false); }}
                        className="flex w-full items-center gap-2 px-3 py-1.5 text-left text-sm text-neutral-700 hover:bg-neutral-100 dark:text-neutral-300 dark:hover:bg-neutral-700"
                      >
                        <Pencil className="h-3.5 w-3.5" />
                        {t('task.editLabel')}
                      </button>
                      <button
                        onClick={() => { onDelete(); setShowMenu(false); }}
                        className="flex w-full items-center gap-2 px-3 py-1.5 text-left text-sm text-red-600 hover:bg-red-50 dark:text-red-400 dark:hover:bg-red-900/20"
                      >
                        <Trash2 className="h-3.5 w-3.5" />
                        {t('common.delete')}
                      </button>
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* Reply input */}
            {isReplying && (
              <div className="mt-3">
                <CommentInput
                  value={replyValue}
                  onChange={onReplyChange}
                  onSubmit={onSubmitReply}
                  isSubmitting={isSubmittingReply}
                  placeholder={t('task.writeReplyPlaceholder')}
                  autoFocus
                  onCancel={onCancelReply}
                />
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Replies */}
      {replies.length > 0 && (
        <div className="mt-3 space-y-3">
          {replies.map(reply => {
            const nestedReplies = allComments.filter(c => c.parentId === reply.id);
            return (
              <CommentItem
                key={reply.id}
                comment={reply}
                replies={nestedReplies}
                isEditing={editingId === reply.id}
                editContent={editContentForReply}
                onEditContentChange={onEditContentChangeForReply}
                onStartEdit={() => onStartEditComment(reply)}
                onCancelEdit={onCancelEditForReply}
                onSaveEdit={() => onSaveEditForReply(reply.id)}
                onDelete={() => onDeleteComment(reply.id)}
                onStartReply={() => {}} // Disable nested replies for simplicity
                isReplying={false}
                replyValue=""
                onReplyChange={() => {}}
                onSubmitReply={() => {}}
                onCancelReply={() => {}}
                isSubmittingReply={false}
                depth={depth + 1}
                allComments={allComments}
                onDeleteComment={onDeleteComment}
                onStartEditComment={onStartEditComment}
                editingId={editingId}
                onEditContentChangeForReply={onEditContentChangeForReply}
                onSaveEditForReply={onSaveEditForReply}
                onCancelEditForReply={onCancelEditForReply}
                editContentForReply={editContentForReply}
              />
            );
          })}
        </div>
      )}
    </div>
  );
}
